declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate: {
        TranslateElement: new (options: { pageLanguage: string; autoDisplay: boolean }, id: string) => void;
      };
    };
  }
}

let loadPromise: Promise<void> | null = null;
const TRANSLATE_COOKIE_NAME = 'googtrans';
const SAVED_LANGUAGE_KEY = 'learnaihub-language';

const getCookieValue = (name: string) => {
  const match = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`));

  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : '';
};

const getCookieDomains = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const rootDomain = parts.length > 2 ? `.${parts.slice(-2).join('.')}` : `.${hostname}`;

  return ['', hostname, `.${hostname}`, rootDomain];
};

const clearTranslateCookies = () => {
  getCookieDomains().forEach((domain) => {
    const domainPart = domain ? ` domain=${domain};` : '';
    document.cookie = `${TRANSLATE_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;${domainPart}`;
    document.cookie = `${TRANSLATE_COOKIE_NAME}=; max-age=0; path=/;${domainPart}`;
  });
};

export const getCurrentGoogleTranslateLanguage = (): string => {
  const translateCookie = getCookieValue(TRANSLATE_COOKIE_NAME);
  const cookieLanguage = translateCookie.split('/').filter(Boolean).pop();

  if (cookieLanguage && cookieLanguage !== 'en') {
    return cookieLanguage;
  }

  return localStorage.getItem(SAVED_LANGUAGE_KEY) || 'en';
};

const waitForTranslateCombo = async (): Promise<HTMLSelectElement | null> => {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    const select = document.querySelector<HTMLSelectElement>('.goog-te-combo');
    if (select) {
      return select;
    }

    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  }

  return null;
};

export const preloadGoogleTranslate = (): Promise<void> => loadGoogleTranslate();

export const loadGoogleTranslate = (): Promise<void> => {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId: number | undefined;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      resolve();
    };

    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      loadPromise = null;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      reject(error);
    };

    const initTranslate = () => {
      try {
        if (!window.google?.translate) {
          fail(new Error('Google Translate is unavailable'));
          return;
        }

        if (!document.querySelector('.goog-te-combo')) {
          new window.google.translate.TranslateElement(
            { pageLanguage: 'en', autoDisplay: false },
            'google_translate_element',
          );
        }

        finish();
      } catch (error) {
        fail(error instanceof Error ? error : new Error('Google Translate failed to initialize'));
      }
    };

    if (window.google?.translate) {
      initTranslate();
      return;
    }

    window.googleTranslateElementInit = initTranslate;

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src*="translate.google.com/translate_a/element.js"]',
    );

    timeoutId = window.setTimeout(() => {
      fail(new Error('Google Translate took too long to load'));
    }, 12000);

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      script.onerror = () => fail(new Error('Failed to load Google Translate'));
      document.head.appendChild(script);
    } else {
      existingScript.addEventListener('load', initTranslate, { once: true });
      existingScript.addEventListener('error', () => fail(new Error('Failed to load Google Translate')), { once: true });
    }
  });

  return loadPromise;
};

export const changeGoogleTranslateLanguage = async (gtCode: string): Promise<void> => {
  localStorage.setItem(SAVED_LANGUAGE_KEY, gtCode);

  if (gtCode === 'en') {
    clearTranslateCookies();
    window.location.reload();
    return;
  }

  await loadGoogleTranslate();
  const select = await waitForTranslateCombo();
  if (!select) {
    return;
  }

  select.value = gtCode;
  select.dispatchEvent(new Event('change'));
};
